import { segmentIdMaxLength } from '@openx/types/targeting/contextualSegments';
import type { SegmentsOption } from '@openx/types/targeting/customVariables';
import type { InvalidItemsError, ValidationResult } from '../../shared/BulkAdd/types';
import {
  type ContextualInclusionType,
  type ContextualSegmentIdentity,
  getContextualSegmentKey,
  resolveBulkSegmentProvider,
} from './enrichment';

export type BulkContextualSegment = ContextualSegmentIdentity & {
  metadata?: SegmentsOption;
};

export const serializeBulkContextualSegment = (segment: ContextualSegmentIdentity): string =>
  getContextualSegmentKey(segment);

/**
 * Parse a serialized contextual segment key back into its component parts.
 *
 * IMPORTANT: This parser is tightly coupled to the format produced by `getContextualSegmentKey`,
 * which currently returns `JSON.stringify([normalizeProviderId(providerId), segmentId])`.
 * If that format changes (e.g., to use objects, delimiters, etc.), this parser will fail
 * and cause bulk-added segments to be silently dropped in `addBulkSegments`.
 */
export const parseBulkContextualSegment = (value: string): ContextualSegmentIdentity | undefined => {
  try {
    const parsed: unknown = JSON.parse(value);
    if (!Array.isArray(parsed) || parsed.length !== 2) return undefined;
    const [providerId, segmentId] = parsed;
    return typeof providerId === 'string' && typeof segmentId === 'string' ? { providerId, segmentId } : undefined;
  } catch {
    return undefined;
  }
};

export const formatBulkContextualSegmentForDisplay = (value: string): string => {
  const segment = parseBulkContextualSegment(value);
  return segment ? segment.segmentId : value;
};

export const validateBulkContextualSegments = (
  rawIds: string[],
  catalogSegments: SegmentsOption[],
  accountProviderIds: string[],
  existingSelections: Map<string, ContextualInclusionType>,
  inclusionType: ContextualInclusionType,
  t: (key: string, options?: Record<string, unknown>) => string
): ValidationResult & { resolved: BulkContextualSegment[] } => {
  // Build lookup: segmentId → catalog entries
  const catalogBySegmentId = new Map<string, SegmentsOption[]>();
  catalogSegments.forEach(segment => {
    const entries = catalogBySegmentId.get(segment.id) ?? [];
    entries.push(segment);
    catalogBySegmentId.set(segment.id, entries);
  });

  const overlengthIds: string[] = [];
  const duplicateIds: string[] = [];
  const alreadySelectedIds: string[] = [];
  const unresolvedIds: string[] = [];
  const resolved: BulkContextualSegment[] = [];
  const processedKeys = new Set<string>();

  rawIds.forEach(segmentId => {
    // Validate length
    if (segmentId.length > segmentIdMaxLength) {
      overlengthIds.push(segmentId);
      return;
    }

    const resolution = resolveBulkSegmentProvider(segmentId, catalogBySegmentId, accountProviderIds);
    if (!resolution) {
      unresolvedIds.push(segmentId);
      return;
    }
    const { providerId, metadata } = resolution;
    const segmentKey = getContextualSegmentKey({ providerId, segmentId });

    // Check duplicates (within this bulk batch)
    if (processedKeys.has(segmentKey)) {
      duplicateIds.push(segmentId);
      return;
    }

    // Check if already selected in draft with the same mode
    // Allow opposite mode so it can be moved (like the normal item action does)
    if (existingSelections.get(segmentKey) === inclusionType) {
      alreadySelectedIds.push(segmentId);
      return;
    }

    processedKeys.add(segmentKey);
    resolved.push({ providerId, segmentId, metadata });
  });

  const errors: InvalidItemsError[] = [];

  if (overlengthIds.length > 0) {
    errors.push({
      error: t('Length of each ID cannot exceed {limit} characters.', { limit: segmentIdMaxLength }),
      invalidItems: overlengthIds,
    });
  }

  if (unresolvedIds.length > 0) {
    errors.push({
      error: t('Cannot determine a contextual provider.'),
      invalidItems: unresolvedIds,
    });
  }

  if (duplicateIds.length > 0) {
    errors.push({
      error: t('Duplicates in input.'),
      invalidItems: duplicateIds,
    });
  }

  if (alreadySelectedIds.length > 0) {
    errors.push({
      error: t('Already selected.'),
      invalidItems: alreadySelectedIds,
    });
  }

  const validItems = resolved.map(serializeBulkContextualSegment);

  return { errors, validItems, resolved };
};
